"use client";

import { useEffect, useState } from "react";

export default function Home() {
  const [time, setTime] = useState<string>("");
  const [uptime, setUptime] = useState(0);

  useEffect(() => {
    const updateTime = () => {
      setTime(new Date().toLocaleTimeString("en-US", { hour12: false }));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setUptime((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatUptime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0a0a0b] font-mono text-[#e4e4e7] selection:bg-emerald-500/30">
      {/* Animated grid background */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, #fff 1px, transparent 1px),
              linear-gradient(to bottom, #fff 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      {/* Gradient orbs */}
      <div className="pointer-events-none absolute -left-40 -top-40 h-80 w-80 rounded-full bg-emerald-500/10 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 h-80 w-80 rounded-full bg-cyan-500/10 blur-[120px]" />

      {/* Main content */}
      <main className="relative flex min-h-screen flex-col items-center justify-center px-6">
        {/* Status badge */}
        <div className="mb-8 animate-[fadeIn_0.6s_ease-out]">
          <div className="flex items-center gap-2.5 rounded-full border border-emerald-500/20 bg-emerald-500/5 px-4 py-2 backdrop-blur-sm">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
            </span>
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-emerald-400">
              All Systems Operational
            </span>
          </div>
        </div>

        {/* Logo & Title */}
        <div className="mb-12 text-center animate-[fadeIn_0.6s_ease-out_0.1s_both]">
          <div className="mb-6 flex items-center justify-center gap-3">
            {/* Custom Logo */}
            <div className="relative">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 shadow-2xl">
                <svg
                  viewBox="0 0 32 32"
                  fill="none"
                  className="h-8 w-8"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 8h20v3H6zM6 14h14v3H6zM6 20h18v3H6z"
                    fill="url(#logo-gradient)"
                  />
                  <circle cx="26" cy="21.5" r="3" fill="#10b981" />
                  <defs>
                    <linearGradient
                      id="logo-gradient"
                      x1="6"
                      y1="8"
                      x2="26"
                      y2="23"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6ee7b7" />
                      <stop offset="1" stopColor="#06b6d4" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-[#0a0a0b] bg-emerald-500" />
            </div>
          </div>

          <h1 className="mb-3 bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-4xl font-bold tracking-tight text-transparent sm:text-5xl">
            Frinso API
          </h1>
          <p className="text-lg text-zinc-500">
            IoT Platform Backend Services
          </p>
        </div>

        {/* Stats Grid */}
        <div className="mb-12 grid w-full max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4 animate-[fadeIn_0.6s_ease-out_0.2s_both]">
          {[
            { label: "Version", value: "v1.0.0" },
            { label: "Environment", value: "Production" },
            { label: "Server Time", value: time || "--:--:--" },
            { label: "Session", value: formatUptime(uptime) },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className="group rounded-xl border border-zinc-800/50 bg-zinc-900/30 p-4 backdrop-blur-sm transition-all duration-300 hover:border-zinc-700 hover:bg-zinc-900/50"
              style={{ animationDelay: `${0.3 + i * 0.05}s` }}
            >
              <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.2em] text-zinc-600">
                {stat.label}
              </p>
              <p className="font-mono text-sm text-zinc-300 transition-colors group-hover:text-white">
                {stat.value}
              </p>
            </div>
          ))}
        </div>

        {/* API Endpoints */}
        <div className="w-full max-w-2xl animate-[fadeIn_0.6s_ease-out_0.3s_both]">
          <h2 className="mb-4 text-[10px] font-medium uppercase tracking-[0.25em] text-zinc-600">
            Quick Reference
          </h2>
          <div className="space-y-2">
            {[
              { method: "GET", path: "/api/health", desc: "Health check endpoint" },
              { method: "GET", path: "/api/v1/devices", desc: "List all devices" },
              { method: "POST", path: "/api/v1/auth/login", desc: "Authentication" },
              { method: "WS", path: "/mqtt", desc: "MQTT WebSocket connection" },
            ].map((endpoint, i) => (
              <div
                key={endpoint.path}
                className="group flex items-center gap-4 rounded-lg border border-zinc-800/30 bg-zinc-900/20 px-4 py-3 transition-all duration-300 hover:border-zinc-700/50 hover:bg-zinc-900/40"
              >
                <span
                  className={`rounded px-2 py-0.5 text-[10px] font-bold tracking-wider ${
                    endpoint.method === "GET"
                      ? "bg-emerald-500/10 text-emerald-400"
                      : endpoint.method === "POST"
                        ? "bg-amber-500/10 text-amber-400"
                        : "bg-cyan-500/10 text-cyan-400"
                  }`}
                >
                  {endpoint.method}
                </span>
                <code className="flex-1 text-sm text-zinc-400 transition-colors group-hover:text-zinc-200">
                  {endpoint.path}
                </code>
                <span className="text-xs text-zinc-600 transition-colors group-hover:text-zinc-500">
                  {endpoint.desc}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-16 text-center animate-[fadeIn_0.6s_ease-out_0.4s_both]">
          <div className="mb-3 flex items-center justify-center gap-6">
            <a
              href="/api/docs"
              className="text-xs font-medium uppercase tracking-[0.15em] text-zinc-500 transition-colors hover:text-emerald-400"
            >
              API Docs
            </a>
            <span className="h-1 w-1 rounded-full bg-zinc-800" />
            <a
              href="/api/health"
              className="text-xs font-medium uppercase tracking-[0.15em] text-zinc-500 transition-colors hover:text-emerald-400"
            >
              Health Check
            </a>
            <span className="h-1 w-1 rounded-full bg-zinc-800" />
            <a
              href="https://github.com/frinso"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium uppercase tracking-[0.15em] text-zinc-500 transition-colors hover:text-emerald-400"
            >
              GitHub
            </a>
          </div>
          <p className="text-[10px] tracking-[0.2em] text-zinc-700">
            © 2025 FRINSO TECHNOLOGIES • WATER • ENERGY • AUTOMATION
          </p>
        </footer>
      </main>

      {/* Corner decorations */}
      <div className="pointer-events-none absolute left-4 top-4 h-12 w-12 border-l border-t border-zinc-800/50" />
      <div className="pointer-events-none absolute right-4 top-4 h-12 w-12 border-r border-t border-zinc-800/50" />
      <div className="pointer-events-none absolute bottom-4 left-4 h-12 w-12 border-b border-l border-zinc-800/50" />
      <div className="pointer-events-none absolute bottom-4 right-4 h-12 w-12 border-b border-r border-zinc-800/50" />

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
